import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Building2, Zap, Leaf, Globe, ArrowLeft, ExternalLink } from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { useSEO } from '../hooks/useSEO';

const partnersData = [
  {
    id: 'u-cloud24',
    name: 'U-Cloud24',
    website: 'https://u-cloud24.com',
    logo: '☁️',
    shortDesc: 'partner_ucloud_short',
    fullDesc: 'partner_ucloud_full',
    role: 'partner_ucloud_role',
    expertise: [
      'partner_ucloud_expertise_cloud',
      'partner_ucloud_expertise_ai',
      'partner_ucloud_expertise_security',
      'partner_ucloud_expertise_compliance',
    ],
    areas: [
      'partner_ucloud_area_cloud',
      'partner_ucloud_area_devops',
      'partner_ucloud_area_ai',
      'partner_ucloud_area_security',
    ],
    roles: [
      'partner_ucloud_role_1',
      'partner_ucloud_role_2',
      'partner_ucloud_role_3',
      'partner_ucloud_role_4',
    ],
    icon: 'Cloud',
  },
  {
    id: 'craft-me',
    name: 'Craft-Me',
    website: 'https://craft-me.com',
    logo: '🎬',
    shortDesc: 'partner_craftme_short',
    fullDesc: 'partner_craftme_full',
    role: 'partner_craftme_role',
    expertise: [
      'partner_craftme_expertise_video',
      'partner_craftme_expertise_visual',
      'partner_craftme_expertise_campaigns',
      'partner_craftme_expertise_communication',
    ],
    areas: [
      'partner_craftme_area_video',
      'partner_craftme_area_content',
      'partner_craftme_area_social',
      'partner_craftme_area_creative',
    ],
    roles: [
      'partner_craftme_role_1',
      'partner_craftme_role_2',
      'partner_craftme_role_3',
      'partner_craftme_role_4',
    ],
    icon: 'Film',
  },
  {
    id: 'daos',
    name: 'DAOS',
    website: 'https://daos.com.ua',
    logo: '💚',
    shortDesc: 'partner_daos_short',
    fullDesc: 'partner_daos_full',
    role: 'partner_daos_role',
    expertise: [
      'partner_daos_expertise_1',
      'partner_daos_expertise_2',
      'partner_daos_expertise_3',
      'partner_daos_expertise_4',
    ],
    areas: [
      'partner_daos_area_1',
      'partner_daos_area_2',
      'partner_daos_area_3',
      'partner_daos_area_4',
    ],
    roles: [
      'partner_daos_role_1',
      'partner_daos_role_2',
      'partner_daos_role_3',
      'partner_daos_role_4',
    ],
    icon: Globe,
  },
];

export default function PartnerDetail() {
  const { t } = useLanguage();
  const { id } = useParams();

  const partner = partnersData.find(p => p.id === id);
  useSEO({
    title: partner ? `${partner.name} — Partner` : 'Partner',
    description: partner
      ? `Learn about ${partner.name}, a partner of GRAWEO Charitable Foundation supporting our humanitarian mission.`
      : 'Partner of GRAWEO Charitable Foundation.',
  });

  if (!partner) {
    return (
      <div className="bg-white min-h-screen">
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl font-bold font-manrope text-gray-900 mb-4">{t('not_found')}</h1>
            <p className="text-gray-600 mb-8">{t('partner_not_found')}</p>
            <Link to="/partners">
              <Button variant="primary">{t('back_to_partners')}</Button>
            </Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Back Button */}
      <section className="bg-gray-50 py-6 border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/partners">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('back_to_partners')}
            </Button>
          </Link>
        </div>
      </section>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-graweo-chocolate to-graweo-olive text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-8 mb-8">
            <div className="text-7xl flex-shrink-0">{partner.logo}</div>
            <div className="flex-1">
              <h1 className="text-4xl sm:text-5xl font-bold font-manrope mb-4">{partner.name}</h1>
              <p className="text-xl text-gray-100 mb-4">{t(partner.role)}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Full Description */}
          <Card className="p-8 sm:p-12 mb-12">
            <h2 className="text-3xl font-bold font-manrope text-gray-900 mb-6">{t('about_partner')}</h2>
            <p className="text-lg text-gray-700 leading-relaxed">{t(partner.fullDesc)}</p>
          </Card>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {/* Expertise */}
            <Card className="p-8">
              <h3 className="text-2xl font-bold font-manrope text-gray-900 mb-6">
                {t('partners_expertise')}
              </h3>
              <ul className="space-y-4">
                {partner.expertise.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-3 h-3 rounded-full bg-graweo-chocolate mt-2 flex-shrink-0" />
                    <span className="text-gray-700">{t(item)}</span>
                  </li>
                ))}
              </ul>
            </Card>

            {/* Areas of Activity */}
            <Card className="p-8">
              <h3 className="text-2xl font-bold font-manrope text-gray-900 mb-6">
                {t('partners_areas')}
              </h3>
              <div className="flex flex-wrap gap-3">
                {partner.areas.map((area, idx) => (
                  <span
                    key={idx}
                    className="inline-block px-4 py-2 bg-graweo-terra/10 text-graweo-terra rounded-full text-sm font-medium"
                  >
                    {t(area)}
                  </span>
                ))}
              </div>
            </Card>
          </div>

          {/* Role in Ecosystem */}
          <Card className="p-8 sm:p-12 mb-12">
            <h3 className="text-2xl font-bold font-manrope text-gray-900 mb-8">
              {t('partners_role_ecosystem')}
            </h3>
            <ul className="space-y-4">
              {partner.roles.map((role, idx) => {
                const icons = [
                  { Icon: Zap, color: '#C76D46' },
                  { Icon: Leaf, color: '#828960' },
                  { Icon: Building2, color: '#403628' },
                  { Icon: Globe, color: '#D4C6AE' },
                ];
                const { Icon, color } = icons[idx];
                return (
                  <li key={idx} className="flex items-start gap-3">
                    <Icon className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color }} />
                    <span className="text-gray-700 text-lg">{t(role)}</span>
                  </li>
                );
              })}
            </ul>
          </Card>

          {/* CTA */}
          <div className="flex gap-4">
            <a
              href={partner.website}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1"
            >
              <Button variant="primary" className="w-full">
                {t('partners_visit_website')}
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <Link to="/contacts" className="flex-1">
              <Button variant="secondary" className="w-full">
                {t('get_in_touch')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
